<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .ticket-orders-container
    //- Page Header
    .page-header
      h1.page-title My Tickets
      .header-actions
        button(@click="router.push('/web/sticker/checkout')" type="button").cart-button
          img(src="@/assets/icons/shopping-cart.svg" alt="Cart")

    //- Tab Navigation
    .tabs-section
      .tabs-wrapper
        button(
          @click="activeTab = 'upcoming'"
          :class="{ active: activeTab === 'upcoming' }"
          type="button"
        ).tab-button
          span UPCOMING
          .tab-badge(v-if="upcomingCount > 0") {{ upcomingCount }}
        button(
          @click="activeTab = 'past'"
          :class="{ active: activeTab === 'past' }"
          type="button"
        ).tab-button PAST TRANSACTIONS

    //- Search and Filter Section
    .search-section
      .search-wrapper
        .search-input-container
          img(src="@/assets/icons/searchIcon.svg" alt="Search").search-icon
          input(
            v-model="state.searchTerm"
            placeholder="Search Tickets"
            @input="handleSearchInput"
            type="text"
          ).search-input
        button(@click="toggleFilterPanel" type="button").filter-button
          img(src="@/assets/icons/filterIcon.svg" alt="Filter")
          span Filter

    //- Filter Panel
    .filter-panel(v-if="showFilterPanel")
      .filter-header
        h3.filter-title Filter by Type
        button(@click="clearAllFilters" type="button").clear-filters-button Clear All

      .filter-group
        label(
          v-for="type in filterTypes"
          :key="type.value"
          :class="{ active: state.selectedTypes.includes(type.value) }"
        ).filter-checkbox
          input(
            type="checkbox"
            :value="type.value"
            v-model="state.selectedTypes"
            @change="applyFilters"
          )
          span {{ type.label }}

    //- Loading State
    web-page-skeleton(v-if="state.isLoading && state.orders.length === 0" :sections="1" :cardsPerSection="6")

    //- Content
    .content-section(v-if="!state.isLoading || state.orders.length > 0")
      //- Upcoming Tab - List View
      template(v-if="activeTab === 'upcoming'")
        .upcoming-list(v-if="state.filteredOrders.length")
          .upcoming-item(
            v-for="order in state.filteredOrders"
            :key="order.purchaseId"
            @click="goToTicket(order.id)"
          )
            .upcoming-date
              .date-month {{ formatMonth(order.eventDate) }}
              .date-day {{ formatDay(order.eventDate) }}
            .upcoming-image
              img(:src="order.url" :alt="order.title")
            .upcoming-details
              .upcoming-time {{ formatEventTime(order.eventDate, order.eventDateTimeZone) }}
              .upcoming-title {{ order.title }}
            .upcoming-arrow
              img(src="@/assets/icons/chevron-right.svg" alt="View")

      //- Past Transactions Tab - 2 Column Grid
      template(v-else)
        .past-grid(v-if="state.filteredOrders.length")
          .past-card(
            v-for="order in state.filteredOrders"
            :key="order.purchaseId"
            @click="goToSticker(order.id)"
          )
            .past-card-image
              img(:src="order.banner || order.url" :alt="order.title")
              .type-badge-overlay(v-if="order.campaignType === 'Sweepstakes' || order.campaignType === 'Ticket'")
                .type-badge.badge-both BOTH
            .past-card-title {{ order.title }}
            .past-card-date {{ formatPurchaseDate(order.purchaseDate) }}
            .past-card-footer
              .past-card-price ${{ formatPrice(order.purchasePrice) }}
              .campaign-badge(:class="getBadgeClass(order.campaignType)") {{ getCampaignLabel(order.campaignType) }}

      //- Load More button (only show if there are items)
      .load-more-section(v-if="state.hasMoreItems && !state.isLoading && state.filteredOrders.length > 0")
        button(
          @click="loadMore"
          :disabled="state.isLoadingMore"
          type="button"
        ).load-more-button
          span(v-if="!state.isLoadingMore") Load More
          span(v-else) Loading...

    //- Empty State
    .empty-state(v-if="!state.isLoading && state.filteredOrders.length === 0")
      .empty-state-icon {{ activeTab === 'upcoming' ? '🎟️' : '📦' }}
      h3.empty-state-title {{ state.searchTerm ? 'No orders found' : (activeTab === 'upcoming' ? 'No upcoming tickets' : 'No past transactions') }}
      p.empty-state-text(v-if="state.searchTerm") Try adjusting your search terms
      p.empty-state-text(v-else) {{ activeTab === 'upcoming' ? "You don't have any upcoming event tickets" : "You don't have any past transactions" }}

</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, reactive, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { fromJsonToTimezone, getTimezoneInfo } from '@/utils/dateUtils';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import WebPageSkeleton from '../components/WebPageSkeleton.vue';
import gqlRequest from '../dashboard/gql-request';

dayjs.extend(utc);

export default defineComponent({
  name: 'MyTicketOrders',
  components: { WebAppLayout, WebPageSkeleton },
  setup() {
    const {
      route, router, loading, getPurchasedStickersHistory,
    } = gqlRequest();

    const store = useStore();
    const activeTab = ref<'upcoming' | 'past'>('upcoming');
    const showFilterPanel = ref(false);

    const filterTypes = [
      { label: 'Tickets', value: 'Ticket' },
      { label: 'Free', value: 'Free' },
      { label: 'Glow', value: 'Glow' },
      { label: 'Campaigns', value: 'Campaign' },
      { label: 'Sweepstakes', value: 'Sweepstakes' },
    ];

    const state = reactive({
      orders: [] as any[],
      filteredOrders: [] as any[],
      searchTerm: '',
      selectedTypes: ['Ticket'] as string[],
      currentPage: 1,
      itemsPerPage: 12,
      hasMoreItems: true,
      isLoading: false,
      isLoadingMore: false,
    });

    const upcomingCount = computed(() => state.orders.filter((order: any) => {
      if (!order.eventDate) return false;
      return dayjs(order.eventDate).isAfter(dayjs());
    }).length);

    let searchDebounceTimer: number | null = null;

    function handleSearchInput() {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }

      searchDebounceTimer = setTimeout(() => {
        state.currentPage = 1;
        state.hasMoreItems = true;
        getOrders(1, state.searchTerm, false);
      }, 500);
    }

    async function getOrders(page = 1, searchString = '', append = false) {
      if (page === 1) {
        state.isLoading = true;
        loading.value = true;
      } else {
        state.isLoadingMore = true;
      }

      try {
        const userId = store.getters['users/me'].id;

        if (!userId) {
          state.orders = [];
          state.filteredOrders = [];
          state.hasMoreItems = false;
          return;
        }

        const skip = (page - 1) * state.itemsPerPage;

        // Backend supports: Ticket, Campaign, Sweepstakes, MatchCampaign
        const supportedTypes = state.selectedTypes.filter((t) => ['Ticket', 'Campaign', 'Sweepstakes', 'MatchCampaign'].includes(t));

        // If only unsupported types selected, show empty state
        if (supportedTypes.length === 0) {
          if (!append) {
            state.orders = [];
            state.filteredOrders = [];
          }
          state.hasMoreItems = false;
          return;
        }

        // Build timeFilter based on active tab
        const timeFilterValue = activeTab.value === 'upcoming' ? 'upcoming'
          : activeTab.value === 'past' ? 'past'
            : undefined;

        // Use first selected type (backend doesn't support multiple types yet)
        const typeFilterValue = supportedTypes.length > 0 ? supportedTypes[0] : undefined;

        const result = await getPurchasedStickersHistory(
          'User',
          userId,
          skip,
          state.itemsPerPage,
          searchString,
          typeFilterValue, // Backend filters by type
          timeFilterValue, // Backend filters by time
        );

        const items = result || [];

        const transformedOrders = items.map((item: any) => ({
          ...item.sticker,
          purchasePrice: item.purchasePrice,
          purchaseDate: item.purchaseDate,
          purchaseId: item.id,
        }));

        if (append) {
          state.orders = [...state.orders, ...transformedOrders];
        } else {
          state.orders = transformedOrders;
        }

        // Since backend already filtered, we can directly use results
        // Still need client-side sort for Upcoming (by event date) since backend returns by purchase date
        if (activeTab.value === 'upcoming') {
          state.orders.sort((a: any, b: any) => {
            const dateA = dayjs(a.eventDate);
            const dateB = dayjs(b.eventDate);
            return dateA.diff(dateB);
          });
        }

        state.filteredOrders = state.orders;

        state.hasMoreItems = items.length === state.itemsPerPage;
        state.currentPage = page;
      } catch (error) {
        console.error('[MyTicketOrders] getOrders error:', error);
        if (!append) {
          state.orders = [];
          state.filteredOrders = [];
        }
        state.hasMoreItems = false;
      } finally {
        state.isLoading = false;
        state.isLoadingMore = false;
        loading.value = false;
      }
    }

    function loadMore() {
      const nextPage = state.currentPage + 1;
      getOrders(nextPage, state.searchTerm, true);
    }

    function goToTicket(stickerId: string) {
      router.push(`/web/ticket/${stickerId}/detail`);
    }

    function goToSticker(stickerId: string) {
      router.push(`/web/sticker/${stickerId}`);
    }

    function formatMonth(date: string) {
      return dayjs(date).format('MMM').toUpperCase();
    }

    function formatDay(date: string) {
      return dayjs(date).format('DD');
    }

    function formatEventTime(date: string, timezone?: string) {
      const tz = timezone || null;
      const converted = fromJsonToTimezone(date, tz);
      if (!converted) return dayjs.utc(date).format('ddd - h:mm A [UTC]');
      const abbr = tz ? getTimezoneInfo(tz).abbr : 'UTC';
      return converted.format(`ddd - h:mm A [${abbr}]`);
    }

    function formatPurchaseDate(date: string) {
      return dayjs(date).format('MMM D, YYYY');
    }

    function formatPrice(price: number | string) {
      const numPrice = typeof price === 'string' ? parseFloat(price) : price;
      return numPrice.toFixed(2);
    }

    function getBadgeClass(campaignType: string) {
      const type = campaignType?.toLowerCase();
      if (type === 'ticket') return 'badge-ticket';
      if (type === 'sweepstakes') return 'badge-sweepstakes';
      return 'badge-glow';
    }

    function getCampaignLabel(campaignType: string) {
      return campaignType || 'Campaign';
    }

    function toggleFilterPanel() {
      showFilterPanel.value = !showFilterPanel.value;
    }

    function clearAllFilters() {
      state.selectedTypes = ['Ticket'];
      applyFilters();
    }

    function applyFilters() {
      state.currentPage = 1;
      state.hasMoreItems = true;
      getOrders(1, state.searchTerm, false);
    }

    watch(activeTab, () => {
      state.currentPage = 1;
      state.hasMoreItems = true;
      getOrders(1, state.searchTerm, false);
    });

    onMounted(async () => {
      await getOrders(1, '', false);
    });

    return {
      route,
      router,
      loading,
      activeTab,
      state,
      showFilterPanel,
      filterTypes,
      upcomingCount,
      handleSearchInput,
      loadMore,
      goToTicket,
      goToSticker,
      formatMonth,
      formatDay,
      formatEventTime,
      formatPurchaseDate,
      formatPrice,
      getBadgeClass,
      getCampaignLabel,
      toggleFilterPanel,
      clearAllFilters,
      applyFilters,
    };
  },
});
</script>

<style scoped lang="scss">
.ticket-orders-container {
  padding: 0 !important;
  min-height: 100vh;
  background: #ffffff;
  max-width: 1200px;
}

.page-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.cart-button,
.notification-button {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  img {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: #e5e7eb;
  }
}

.tabs-section {
  padding: 0 24px;
  border-bottom: 2px solid #e5e7eb;
}

.tabs-wrapper {
  display: flex;
  gap: 0;
}

.tab-button {
  padding: 16px 20px;
  border: none;
  border-bottom: 3px solid transparent;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.5px;

  &.active {
    color: #0d9488;
    border-bottom-color: #0d9488;
  }

  &:hover:not(.active) {
    color: #1a1a1a;
  }
}

.tab-badge {
  background: #0d9488;
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  min-width: 20px;
  text-align: center;
}

.search-section {
  padding: 16px 24px;
  background: #f9fafb;
}

.search-wrapper {
  display: flex;
  gap: 12px;
  align-items: center;
  max-width: 100%;
}

.search-input-container {
  position: relative;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  opacity: 0.4;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1a1a1a;
  background: #ffffff;
  transition: all 0.2s;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #0d9488;
  }
}

.filter-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  img {
    width: 16px;
    height: 16px;
    opacity: 0.6;
  }

  &:hover {
    background: #f9fafb;
    border-color: #0d9488;

    img {
      opacity: 1;
    }
  }
}

.filter-panel {
  padding: 20px 24px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.filter-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.clear-filters-button {
  background: none;
  border: none;
  color: #0d9488;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f0fdfa;
  }
}

.filter-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #0d9488;
  }

  &.active {
    background: #f0fdfa;
    border-color: #0d9488;
    color: #0d9488;
    font-weight: 600;
  }

  &:hover {
    border-color: #0d9488;
  }

  span {
    pointer-events: none;
  }
}

.content-section {
  padding: 0;
}

// Upcoming List View
.upcoming-list {
  padding: 0;
}

.upcoming-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }
}

.upcoming-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 50px;
  flex-shrink: 0;
}

.date-month {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.date-day {
  font-size: 24px;
  font-weight: 900;
  color: #1a1a1a;
  line-height: 1;
}

.upcoming-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.upcoming-details {
  flex: 1;
  min-width: 0;
}

.upcoming-time {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
}

.upcoming-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.upcoming-arrow {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  opacity: 0.3;

  img {
    width: 100%;
    height: 100%;
  }
}

// Past 2-Column Grid View
.past-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 16px 24px;
}

.past-card {
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-4px);
  }
}

.past-card-image {
  position: relative;
  height: 128px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 12px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.type-badge-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
}

.type-badge {
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 6px;
  color: white;

  &.badge-both {
    background: #0d9488;
  }
}

.past-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.past-card-date {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
}

.past-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.past-card-price {
  font-size: 14px;
  font-weight: 700;
  color: #0d9488;
}

.campaign-badge {
  padding: 4px 8px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  text-transform: capitalize;
  border: 1px solid #e5e7eb;

  &.badge-ticket {
    background: #f3f4f6;
    color: #4b5563;
  }

  &.badge-sweepstakes {
    background: #f3f4f6;
    color: #4b5563;
  }

  &.badge-glow {
    background: #f3f4f6;
    color: #4b5563;
  }
}

.load-more-section {
  display: flex;
  justify-content: center;
  padding: 24px;
}

.load-more-button {
  padding: 12px 32px;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: scale(1.02);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.empty-state {
  padding: 80px 24px;
  text-align: center;
}

.empty-state-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
}

.empty-state-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.empty-state-text {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

// Experiences Section
.experiences-section {
  padding: 32px 24px 48px;
  background: #f9fafb;
  margin-top: 0;
}

.experiences-title {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 16px 0;
}

.experiences-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.experience-card {
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-4px);
  }
}

.experience-card-image {
  position: relative;
  height: 128px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 12px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.virtual-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 4px 8px;
  background: #0d9488;
  color: white;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 4px;
}

.experience-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.experience-card-date {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
}

.experience-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.experience-card-price {
  font-size: 14px;
  font-weight: 700;
  color: #0d9488;
}

.experience-badge {
  padding: 4px 8px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  text-transform: capitalize;
  border: 1px solid #e5e7eb;
}
</style>
